import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import AddVehicleForm from './forms/AddVehicleForm';
import AddVehicleHeader from './AddVehicleHeader';
import RouterPaths from '../constants/RouterPaths';
import {
  clearAuction,
  disableAuctionEditing,
  fetchAllVehicleFeatures,
  fetchOrganisationDescription,
  fetchOrganizationGroup,
  fetchOrganizationLocations,
  getDraft,
} from './AddVehicleActions';
import { capitalizeString } from '../common/helpers/capitalizeString';
import {
  Fuel as VehicleFuels,
  Transmission as VehicleTransmissions,
} from '../constants/Vehicle';
import { PhotoUploadState } from '../constants/PhotoUploadState';

function AddVehiclePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: idFromUrl } = useParams();

  const {
    basicData,
    vehicleFeatures,
    allVehicleFeatures,
    firstRender,
    vehicleMedia,
    serviceHistory,
    tyreThreadDepths,
    descriptionAndSettings,
    damages,
    damagesToDelete,
    id: draftId,
    vehicleFound,
    autofill,
  } = useSelector((state) => state.addVehicle.toJS(), isEqual);
  const isBuyer = useSelector((state) => state.auth.get('isBuyer'));
  const isSeller = useSelector((state) => state.auth.get('isSeller'));
  const [hasAccess, setHasAccess] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);

  const isFormDisabled = useMemo(
    () => vehicleFound === null || (vehicleFound === false && autofill !== false),
    [vehicleFound, autofill],
  );
  const isUploading = useMemo(
    () =>
      vehicleMedia?.videos?.some((video) => video.status === PhotoUploadState.PENDING) ||
      vehicleMedia?.photos?.some((photo) => photo.status === PhotoUploadState.PENDING),
    [vehicleMedia],
  );

  const formSaveDraftRef = useRef();
  const formPublishRef = useRef();
  const formRef = useRef();

  const vehicleFeaturesDictionaryIds = useMemo(
    () => vehicleFeatures?.vehicleFeaturesDictionary,
    [vehicleFeatures],
  );

  const vehicleFeaturesCustom = useMemo(
    () => vehicleFeatures?.vehicleFeaturesCustom,
    [vehicleFeatures],
  );

  useEffect(() => {
    dispatch(clearAuction());
    if (idFromUrl) {
      dispatch(getDraft(idFromUrl));
    }
    // On unmount, clear state
    return () => {
      dispatch(clearAuction());
      dispatch(disableAuctionEditing());
    };
  }, [dispatch, idFromUrl]);

  useEffect(() => {
    dispatch(fetchOrganizationLocations());
    dispatch(fetchOrganizationGroup());
    dispatch(fetchAllVehicleFeatures());
    dispatch(fetchOrganisationDescription());
  }, [dispatch]);

  const hasEquivalent = useCallback((value, list) => {
    if (!value || !list) return false;
    if (Array.isArray(list)) {
      return list.includes(value);
    }
    return Object.keys(list).includes(value) || Object.values(list).includes(value);
  }, []);

  const handleSaveDraft = useCallback(() => {
    if (formSaveDraftRef.current) {
      formSaveDraftRef.current();
    }
  }, []);

  const handlePublish = useCallback(() => {
    if (formPublishRef.current) {
      formPublishRef.current();
    }
  }, []);

  const [isScheduledAuction, setIsScheduledAuction] = useState(false);

  const publishButtonLabel = useMemo(
    () => (isScheduledAuction ? 'SCHEDULE OFFER' : 'PUBLISH OFFER'),
    [isScheduledAuction],
  );

  const initialValues = useMemo(() => {
    const currentFormValues = formRef.current?.values;

    const preparedCapFeaturesArr = allVehicleFeatures?.map((item) => {
      if (vehicleFeaturesDictionaryIds?.includes(item?.id)) {
        return item.value;
      }
      return null;
    });

    const getTransmissionValue = () => {
      if (!basicData?.transmission) return '';

      if (basicData.transmission?.value) return basicData.transmission;

      if (hasEquivalent(basicData.transmission, VehicleTransmissions)) {
        return {
          value: Object.keys(VehicleTransmissions).includes(basicData.transmission)
            ? VehicleTransmissions[basicData.transmission]
            : basicData.transmission,
          label: VehicleTransmissions[basicData.transmission] || basicData.transmission,
        };
      }

      return '';
    };

    const getFuelValue = () => {
      if (!basicData?.fuel) return '';

      if (basicData.fuel?.value) return basicData.fuel;

      if (hasEquivalent(basicData.fuel, VehicleFuels)) {
        return { value: basicData.fuel, label: basicData.fuel };
      }

      return '';
    };
    const getColourValue = () => {
      if (!basicData?.colour) return '';

      if (basicData.colour?.value) return basicData.colour;

      if (typeof basicData.colour === 'string') {
        return {
          value: capitalizeString(basicData.colour),
          label: capitalizeString(basicData.colour),
        };
      }

      return '';
    };

    const getVehicleTypeValue = () => {
      if (!basicData?.vehicleType) return '';

      if (basicData.vehicleType?.value) return basicData.vehicleType;

      if (typeof basicData.vehicleType === 'string') {
        return {
          value: capitalizeString(basicData.vehicleType),
          label: capitalizeString(basicData.vehicleType),
        };
      }

      return '';
    };

    const getCarTypeValue = () => {
      if (!basicData?.carType) return '';

      if (basicData.carType?.value) return basicData.carType;

      if (typeof basicData.carType === 'string') {
        return {
          value: capitalizeString(basicData.carType),
          label: capitalizeString(basicData.carType),
        };
      }

      return '';
    };

    const baseInitialValues = {
      id: draftId,
      basicData: {
        registrationPlate: basicData?.registrationPlate || '',
        makeSelect:
          basicData?.makeId && basicData?.makeName
            ? { value: basicData.makeId, label: basicData.makeName }
            : '',
        modelSelect:
          basicData?.modelId && basicData?.modelName
            ? { value: basicData.modelId, label: basicData.modelName }
            : '',
        mileage: basicData?.mileage?.toString() || '',
        year: basicData?.year?.toString() || '',
        mot: basicData?.mot || '',
        derivative: basicData?.derivative || '',
        carType: getCarTypeValue(),
        transmission: getTransmissionValue(),
        engineSize: basicData?.engineSize?.toString() || '',
        fuel: getFuelValue(),
        colour: getColourValue(),
        doors: basicData?.doors?.toString() || '',
        dateOfFirstRegistration: basicData?.dateOfFirstRegistration || '',
        previousKeepersInTotal: basicData?.previousKeepersInTotal?.toString() || '',
        vehicleCapCapId: basicData?.capId || basicData?.vehicleCapCapId || '',
        vin: basicData?.vin ? capitalizeString(basicData.vin) : '',
        v5cLogBook: basicData?.v5cLogBook || '',
        ulezCompliant: basicData?.ulezCompliant || '',
        keys: basicData?.keys || '',
        vehicleType: getVehicleTypeValue(),
        vatQualifying: basicData?.vatQualifying || '',
      },
      tyreThreadDepths: tyreThreadDepths || {
        nearsideFront: '',
        nearsideRear: '',
        offsideFront: '',
        offsideRear: '',
      },
      vehicleMedia: {
        photos: [],
        videos: [],
        photosToDelete: [],
        videosToDelete: [],
        ...((!firstRender && vehicleMedia) || {}),
      },
      serviceHistory: serviceHistory || {
        type: '',
        manualRecords: [{ date: '', serviceType: '', mileage: '', serviceName: '' }],
        photoRecords: [],
        photoRecordsToDelete: [],
      },
      vehicleDamages: {
        damages: damages || [],
        damagesToDelete: damagesToDelete || [],
      },
      vehicleFeatures: {
        vehicleFeaturesDictionary: vehicleFeaturesDictionaryIds?.length
          ? vehicleFeaturesDictionaryIds
          : [],
        vehicleFeaturesCustom: vehicleFeaturesCustom?.length ? vehicleFeaturesCustom : [],
      },
      descriptionAndSettings: {
        description: '',
        locationId: '',
        type: '',
        minimumPriceInPence: 0,
        autoAcceptPriceInPence: null,
        buyNowPriceInPence: null,
        auctionStartDate: null,
        auctionEndDate: null,
        tryingToDealDeliveryDate: '',
        tryingToDealDeliveryDateType: '',
        timeOption: '',
        publishTarget: '',
        vehicleLocation: '',
        startAuctionImmediately: true,
        standardAuctionAndBuyNow: !(
          descriptionAndSettings?.isBuyNowFeatureEnable ||
          descriptionAndSettings?.termsAccepted
        ),
        termsAccepted: false,
        defaultDescription: descriptionAndSettings?.defaultDescription || '',
        ...(descriptionAndSettings || {}),
      },
      capFeatures: vehicleFeaturesDictionaryIds ? preparedCapFeaturesArr : [],
      customFeatures: [],
      newCustomFeature: '',
      damages: [],
    };
    if (currentFormValues && currentFormValues.id === draftId) {
      return {
        ...baseInitialValues,
        basicData: {
          ...baseInitialValues.basicData,
          ...(currentFormValues.basicData || {}),
        },
        tyreThreadDepths: {
          ...baseInitialValues.tyreThreadDepths,
          ...(currentFormValues.tyreThreadDepths || {}),
        },
        serviceHistory: {
          ...baseInitialValues.serviceHistory,
          ...(currentFormValues.serviceHistory || {}),
          photoRecords: baseInitialValues.serviceHistory.photoRecords,
          photoRecordsToDelete: baseInitialValues.serviceHistory.photoRecordsToDelete,
        },
        vehicleFeatures: {
          ...baseInitialValues.vehicleFeatures,
          ...(currentFormValues.vehicleFeatures || {}),
        },
        descriptionAndSettings: {
          ...baseInitialValues.descriptionAndSettings,
          ...(currentFormValues.descriptionAndSettings || {}),
        },
        capFeatures: currentFormValues.capFeatures || baseInitialValues.capFeatures,
        customFeatures: currentFormValues.customFeatures || baseInitialValues.customFeatures,
        newCustomFeature:
          currentFormValues.newCustomFeature || baseInitialValues.newCustomFeature,
        vehicleMedia: baseInitialValues.vehicleMedia,
        vehicleDamages: baseInitialValues.vehicleDamages,
        damages: baseInitialValues.damages,
      };
    }

    return baseInitialValues;
  }, [
    draftId,
    basicData,
    tyreThreadDepths,
    firstRender,
    serviceHistory,
    descriptionAndSettings,
    vehicleMedia,
    damages,
    damagesToDelete,
    vehicleFeaturesDictionaryIds,
    vehicleFeaturesCustom,
    allVehicleFeatures,
    hasEquivalent,
  ]);

  useEffect(() => {
    if (isBuyer && !isSeller) {
      setHasAccess(false);
      return navigate(RouterPaths.DASHBOARD);
    }
    return setHasAccess(true);
  }, [isBuyer, isSeller, navigate]);

  useEffect(() => {
    if (idFromUrl && basicData && vehicleMedia) {
      setIsFormReady(true);
    }
  }, [idFromUrl, basicData, vehicleMedia]);

  useEffect(() => {
    const orgDesc = descriptionAndSettings?.defaultDescription;
    const form = formRef.current;
    if (form && orgDesc && !form.values.descriptionAndSettings?.defaultDescription) {
      form.setFieldValue('descriptionAndSettings.defaultDescription', orgDesc);
    }
  }, [descriptionAndSettings?.defaultDescription]);

  if (idFromUrl && !isFormReady) {
    return <div className="text-center mt-5">Loading draft...</div>;
  }

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {hasAccess && (
        <div className="add-vehicle-page">
          <AddVehicleHeader
            onSaveDraft={handleSaveDraft}
            onPublish={handlePublish}
            isSaving={isSavingDraft}
            isPublishing={isPublishing}
            disabled={isFormDisabled || isUploading}
            publishLabel={publishButtonLabel}
            isScheduledAuction={isScheduledAuction}
          />
          <Formik
            key={idFromUrl || 'new'}
            initialValues={initialValues}
            enableReinitialize={true}
            validateOnBlur
            onSubmit={() => null}
          >
            {(form) => {
              formRef.current = form;

              return (
                <AddVehicleForm
                  form={form}
                  setSavingDraft={setIsSavingDraft}
                  setPublishing={setIsPublishing}
                  saveDraftRef={formSaveDraftRef}
                  publishRef={formPublishRef}
                  setIsScheduledAuction={setIsScheduledAuction}
                />
              );
            }}
          </Formik>
        </div>
      )}
    </>
  );
}

AddVehiclePage.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default AddVehiclePage;
